import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  createDraft,
  deleteDraft,
  getDraft,
  listDrafts,
  listOwnFreeformPolicies,
  promoteDraft,
  updateDraftFreeformText,
} from './draftsApi';

const POLICY_NAME_RE = /^[a-z0-9_-]{1,64}$/;

// A draft either names an existing policy it's editing (`policy` set, of
// the form "{owner}/{name}") or is authoring a brand-new one (`policy`
// null, `name` is what the promoted policy will be called) - see
// DraftOut in schemas_policy.py. Either way, this is what shows up in
// the list.
function draftTitle(draft) {
  if (draft.policy) return draft.policy.toUpperCase();
  if (draft.name) return draft.name.toUpperCase();
  return 'UNTITLED DRAFT';
}

const SUMMARY_MAX_LENGTH = 80;

// `description` is a separate, optional metadata field that this UI
// never sets (see DraftCreate/DraftPatch in schemas_policy.py) - every
// draft this wizard creates carries its actual content in
// `freeform_text` instead, so that's what a listing should summarize.
// Collapsed to one line and capped since freeform_text can run up to
// 10,000 characters.
function draftSummary(draft) {
  const text = draft.freeform_text || draft.description;
  if (!text) return 'NO DESCRIPTION ON FILE';
  const singleLine = text.replace(/\s+/g, ' ').trim();
  if (singleLine.length <= SUMMARY_MAX_LENGTH) return singleLine;
  return `${singleLine.slice(0, SUMMARY_MAX_LENGTH - 1).trimEnd()}\u2026`;
}

function draftLabel(draft) {
  const title = draftTitle(draft);
  return `[DRAFT] ${title} \u2014 ${draftSummary(draft)}`;
}

function validatePolicyName(name) {
  if (!name) return 'a name is required';
  if (!POLICY_NAME_RE.test(name)) {
    return 'must be 1-64 characters: lowercase letters, digits, hyphens (-), and underscores (_) only';
  }
  return null;
}

function validateFreeformText(text) {
  if (!text || !text.trim()) return 'describe what this policy should allow or deny';
  if (text.length > 10_000) return 'must be at most 10,000 characters';
  return null;
}

export default function DraftPolicies({ onError }) {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const username = auth.user?.profile?.preferred_username
    ?? auth.user?.profile?.username
    ?? auth.user?.profile?.sub;

  const [status, setStatus] = useState('loading'); // loading | error | ready
  const [drafts, setDrafts] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  // The "+ NEW DRAFT" wizard. `view` drives which panel below the
  // drafts list (if any) is showing:
  //   list           - just the drafts list + the entry-point button
  //   choose-type    - "new policy" vs. "edit existing policy"
  //   new-policy     - name + requirements text, for a brand-new policy
  //   edit-existing  - pick one of the caller's freeform-eligible
  //                    policies, then edit its requirements text
  const [view, setView] = useState('list');
  const [name, setName] = useState('');
  const [freeformText, setFreeformText] = useState('');
  const [formError, setFormError] = useState('');
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | submitting

  const [eligiblePolicies, setEligiblePolicies] = useState([]);
  const [selectedPolicyKey, setSelectedPolicyKey] = useState('');
  const [policiesStatus, setPoliciesStatus] = useState('idle'); // idle | loading | error | ready
  const [policiesError, setPoliciesError] = useState('');

  // The detail panel for an existing draft (`view === 'detail'`) - view,
  // edit its freeform text, delete it outright, or promote it. Separate
  // from the "+ NEW DRAFT" wizard state above since the two are never
  // shown at once and shouldn't bleed into each other.
  const [detailDraftId, setDetailDraftId] = useState(null);
  const [detailDraft, setDetailDraft] = useState(null);
  const [detailStatus, setDetailStatus] = useState('idle'); // idle | loading | error | ready
  const [detailError, setDetailError] = useState('');
  const [detailFreeformText, setDetailFreeformText] = useState('');
  const [detailFormError, setDetailFormError] = useState('');
  const [detailNotice, setDetailNotice] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving
  const [deleteStatus, setDeleteStatus] = useState('idle'); // idle | confirming | deleting
  const [promoteStatus, setPromoteStatus] = useState('idle'); // idle | promoting

  // A one-line success note shown on the list view after an action
  // (delete/promote) that navigates *away* from the detail panel it was
  // performed on - there'd be nothing left to show the note on
  // otherwise. Cleared on the next list refresh so it can't go stale.
  const [listNotice, setListNotice] = useState('');

  const fetchDrafts = useCallback(async () => {
    setStatus('loading');
    try {
      const result = await listDrafts(accessToken);
      setDrafts(result);
      setStatus('ready');
    } catch (err) {
      setErrorMessage(err.message || 'Could not load drafts. Please try again.');
      setStatus('error');
    }
  }, [accessToken]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  function resetForm() {
    setName('');
    setFreeformText('');
    setFormError('');
    setSubmitStatus('idle');
    setSelectedPolicyKey('');
  }

  // Every place that shows an inline `formError` also raises the fixed
  // bottom error-toast (App.js's `onError`) - the inline text stays put
  // as the detail, the toast is just the "something needs your
  // attention" signal, same as it already is for the config save.
  function reportFormError(message) {
    setFormError(message);
    onError?.();
  }

  function backToList() {
    setView('list');
    resetForm();
  }

  function openChooseType() {
    setView('choose-type');
    setListNotice('');
    resetForm();
  }

  function chooseNewPolicy() {
    setView('new-policy');
    resetForm();
  }

  // --- Draft detail: view/edit/delete/promote an existing draft ---

  const fetchDraftDetail = useCallback(async (draftId) => {
    setDetailStatus('loading');
    setDetailError('');
    try {
      const result = await getDraft(accessToken, draftId);
      setDetailDraft(result);
      setDetailFreeformText(result.freeform_text || '');
      setDetailStatus('ready');
    } catch (err) {
      setDetailError(err.message || 'Could not load this draft. Please try again.');
      setDetailStatus('error');
    }
  }, [accessToken]);

  function resetDetail() {
    setDetailDraftId(null);
    setDetailDraft(null);
    setDetailFreeformText('');
    setDetailFormError('');
    setDetailNotice('');
    setSaveStatus('idle');
    setDeleteStatus('idle');
    setPromoteStatus('idle');
  }

  function openDetail(draftId) {
    setView('detail');
    setListNotice('');
    resetDetail();
    setDetailDraftId(draftId);
    fetchDraftDetail(draftId);
  }

  function backToListFromDetail() {
    setView('list');
    resetDetail();
  }

  // Same inline-error-plus-toast convention as `reportFormError` above,
  // scoped to the detail panel instead of the new-draft form.
  function reportDetailError(message) {
    setDetailFormError(message);
    onError?.();
  }

  async function handleSaveFreeformText(event) {
    event.preventDefault();
    const textError = validateFreeformText(detailFreeformText);
    if (textError) {
      reportDetailError(textError);
      return;
    }
    setDetailFormError('');
    setDetailNotice('');
    setSaveStatus('saving');
    try {
      const updated = await updateDraftFreeformText(accessToken, detailDraftId, detailFreeformText);
      setDetailDraft(updated);
      setDetailFreeformText(updated.freeform_text || '');
      setDetailNotice('THE SCROLL HAS BEEN AMENDED.');
      setSaveStatus('idle');
      // Keep the list's summary text (drawn from freeform_text) in sync
      // for whenever this panel is closed - no need to await it.
      fetchDrafts();
    } catch (err) {
      reportDetailError(err.message || 'Could not save this draft. Please try again.');
      setSaveStatus('idle');
    }
  }

  function startDelete() {
    setDeleteStatus('confirming');
    setDetailFormError('');
    setDetailNotice('');
  }

  function cancelDelete() {
    setDeleteStatus('idle');
  }

  async function confirmDelete() {
    setDeleteStatus('deleting');
    try {
      await deleteDraft(accessToken, detailDraftId);
      backToListFromDetail();
      fetchDrafts();
      setListNotice('THE DRAFT HAS BEEN COMMITTED TO THE FLAMES.');
    } catch (err) {
      reportDetailError(err.message || 'Could not delete this draft. Please try again.');
      setDeleteStatus('idle');
    }
  }

  async function handlePromote() {
    setDetailFormError('');
    setDetailNotice('');
    setPromoteStatus('promoting');
    try {
      const result = await promoteDraft(accessToken, detailDraftId);
      backToListFromDetail();
      fetchDrafts();
      setListNotice(
        `${(result?.policy || '').toUpperCase()} IS NOW LAW, REVISION ${result?.revision?.revision ?? '?'}.`,
      );
    } catch (err) {
      // Most likely `draft_not_compiled` (see promoteDraft's docstring
      // in draftsApi.js) - surfaced verbatim, same as any other error.
      reportDetailError(err.message || 'Could not promote this draft. Please try again.');
      setPromoteStatus('idle');
    }
  }

  const fetchEligiblePolicies = useCallback(async () => {
    setPoliciesStatus('loading');
    try {
      const result = await listOwnFreeformPolicies(accessToken, username);
      setEligiblePolicies(result);
      setSelectedPolicyKey((prev) => {
        if (prev) return prev;
        const first = result[0];
        return first ? `${first.owner}/${first.name}` : '';
      });
      setFreeformText((prev) => prev || result[0]?.freeformText || '');
      setPoliciesStatus('ready');
    } catch (err) {
      setPoliciesError(err.message || 'Could not load policies. Please try again.');
      setPoliciesStatus('error');
    }
  }, [accessToken, username]);

  function chooseEditExisting() {
    setView('edit-existing');
    resetForm();
    fetchEligiblePolicies();
  }

  function handleSelectPolicy(event) {
    const key = event.target.value;
    setSelectedPolicyKey(key);
    // Switching policies replaces the requirements box with *that*
    // policy's current text - the whole point of pre-populating it is
    // to edit a specific policy's requirements, not to carry over
    // whatever was typed for a different one.
    const match = eligiblePolicies.find((p) => `${p.owner}/${p.name}` === key);
    setFreeformText(match?.freeformText || '');
  }

  async function handleSubmitNewPolicy(event) {
    event.preventDefault();
    const nameError = validatePolicyName(name);
    const textError = validateFreeformText(freeformText);
    if (nameError || textError) {
      reportFormError(nameError || textError);
      return;
    }
    setFormError('');
    setSubmitStatus('submitting');
    try {
      await createDraft(accessToken, { name, freeformText });
      backToList();
      fetchDrafts();
    } catch (err) {
      reportFormError(err.message || 'Could not create the draft. Please try again.');
      setSubmitStatus('idle');
    }
  }

  async function handleSubmitEditExisting(event) {
    event.preventDefault();
    const textError = validateFreeformText(freeformText);
    if (!selectedPolicyKey || textError) {
      reportFormError(textError || 'choose a policy to edit');
      return;
    }
    setFormError('');
    setSubmitStatus('submitting');
    try {
      await createDraft(accessToken, { policy: selectedPolicyKey, freeformText });
      backToList();
      fetchDrafts();
    } catch (err) {
      reportFormError(err.message || 'Could not create the draft. Please try again.');
      setSubmitStatus('idle');
    }
  }

  return (
    <div className="terminal-box view-panel">
      <p className="form-title">== DRAFT POLICIES ==</p>

      {status === 'loading' && (
        <p className="dim-line">SCRYING THE ARCHIVES FOR UNFINISHED SCROLLS...</p>
      )}

      {status === 'error' && (
        <>
          <p className="error-line">! {errorMessage}</p>
          <div className="button-row">
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={fetchDrafts}
            >
              RETRY
            </button>
          </div>
        </>
      )}

      {status === 'ready' && view === 'list' && listNotice && (
        <p className="success-line">{listNotice}</p>
      )}

      {status === 'ready' && view === 'list' && drafts.length === 0 && (
        <p className="dim-line">
          NO UNFINISHED SCROLLS ON FILE. THE SCRIPTORIUM AWAITS YOUR QUILL.
        </p>
      )}

      {status === 'ready' && view === 'list' && drafts.length > 0 && (
        <ul className="view-list">
          {drafts.map((draft) => (
            <li key={draft.id}>
              <button
                type="button"
                className="view-list-item"
                onClick={() => openDetail(draft.id)}
              >
                {draftLabel(draft)}
              </button>
            </li>
          ))}
        </ul>
      )}

      {status === 'ready' && view === 'list' && (
        <div className="button-row">
          <button type="button" className="retro-button" onClick={openChooseType}>
            + NEW DRAFT
          </button>
        </div>
      )}

      {status === 'ready' && view === 'choose-type' && (
        <>
          <p className="dim-line">
            THE SCRIPTORIUM AWAITS. SPEAK A NEW LAW INTO BEING, OR HAUL AN OLD
            ONE BACK FOR REVISION.
          </p>
          <div className="button-row">
            <button type="button" className="retro-button" onClick={chooseNewPolicy}>
              NEW POLICY
            </button>
            <button type="button" className="retro-button" onClick={chooseEditExisting}>
              EDIT EXISTING POLICY
            </button>
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToList}
            >
              CANCEL
            </button>
          </div>
        </>
      )}

      {status === 'ready' && view === 'new-policy' && (
        <form onSubmit={handleSubmitNewPolicy}>
          <p className="dim-line">
            DICTATE THY WISHES BELOW; THE SCRIBE SHALL SET THEM DOWN AS LAW.
          </p>

          <label className="form-row" htmlFor="draft-name">
            <span>NAME OF THE LAW:</span>
            <input
              id="draft-name"
              name="draft-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              disabled={submitStatus === 'submitting'}
              spellCheck="false"
              autoFocus
            />
          </label>

          <label className="form-row" htmlFor="draft-freeform-text">
            <span>THY WISHES:</span>
            <textarea
              id="draft-freeform-text"
              name="draft-freeform-text"
              rows={6}
              value={freeformText}
              onChange={(e) => setFreeformText(e.target.value)}
              disabled={submitStatus === 'submitting'}
              placeholder="E.G. LET ALL OF NUMPY PASS UNHARMED, BUT BURNINATE ANY SCIPY BELOW VERSION 1.0"
            />
          </label>

          {formError && <p className="error-line">! {formError}</p>}

          <div className="button-row">
            <button type="submit" className="retro-button" disabled={submitStatus === 'submitting'}>
              {submitStatus === 'submitting' ? 'BURNINATING...' : 'CREATE DRAFT'}
            </button>
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToList}
              disabled={submitStatus === 'submitting'}
            >
              CANCEL
            </button>
          </div>
        </form>
      )}

      {status === 'ready' && view === 'edit-existing' && policiesStatus === 'loading' && (
        <p className="dim-line">CONSULTING THE LEDGER FOR LAWS ALREADY ON THE BOOKS...</p>
      )}

      {status === 'ready' && view === 'edit-existing' && policiesStatus === 'error' && (
        <>
          <p className="error-line">! {policiesError}</p>
          <div className="button-row">
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={fetchEligiblePolicies}
            >
              RETRY
            </button>
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToList}
            >
              CANCEL
            </button>
          </div>
        </>
      )}

      {status === 'ready'
        && view === 'edit-existing'
        && policiesStatus === 'ready'
        && eligiblePolicies.length === 0 && (
        <>
          <p className="dim-line">
            NOT ONE LAW ON THY BOOKS WAS EVER DICTATED TO THE SCRIBE - THEY WERE
            ALL HAND-INSCRIBED BY THE ELDERS THEMSELVES. DECREE A NEW LAW INSTEAD.
          </p>
          <div className="button-row">
            <button type="button" className="retro-button" onClick={chooseNewPolicy}>
              NEW POLICY
            </button>
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToList}
            >
              CANCEL
            </button>
          </div>
        </>
      )}

      {status === 'ready'
        && view === 'edit-existing'
        && policiesStatus === 'ready'
        && eligiblePolicies.length > 0 && (
        <form onSubmit={handleSubmitEditExisting}>
          <p className="dim-line">
            CHOOSE A LAW ALREADY ON THE BOOKS; THE SCRIBE SHALL AMEND IT PER
            THY NEW WISHES.
          </p>

          <label className="form-row" htmlFor="draft-policy-select">
            <span>LAW TO AMEND:</span>
            <select
              id="draft-policy-select"
              name="draft-policy-select"
              value={selectedPolicyKey}
              onChange={handleSelectPolicy}
              disabled={submitStatus === 'submitting'}
            >
              {eligiblePolicies.map((p) => {
                const key = `${p.owner}/${p.name}`;
                return (
                  <option key={key} value={key}>
                    {key.toUpperCase()}
                  </option>
                );
              })}
            </select>
          </label>

          <label className="form-row" htmlFor="draft-freeform-text-edit">
            <span>THY REVISED WISHES:</span>
            <textarea
              id="draft-freeform-text-edit"
              name="draft-freeform-text-edit"
              rows={6}
              value={freeformText}
              onChange={(e) => setFreeformText(e.target.value)}
              disabled={submitStatus === 'submitting'}
            />
          </label>

          {formError && <p className="error-line">! {formError}</p>}

          <div className="button-row">
            <button type="submit" className="retro-button" disabled={submitStatus === 'submitting'}>
              {submitStatus === 'submitting' ? 'BURNINATING...' : 'CREATE DRAFT'}
            </button>
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToList}
              disabled={submitStatus === 'submitting'}
            >
              CANCEL
            </button>
          </div>
        </form>
      )}

      {view === 'detail' && detailStatus === 'loading' && (
        <p className="dim-line">UNROLLING THE SCROLL...</p>
      )}

      {view === 'detail' && detailStatus === 'error' && (
        <>
          <p className="error-line">! {detailError}</p>
          <div className="button-row">
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={() => fetchDraftDetail(detailDraftId)}
            >
              RETRY
            </button>
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToListFromDetail}
            >
              &laquo; BACK
            </button>
          </div>
        </>
      )}

      {view === 'detail' && detailStatus === 'ready' && detailDraft && (
        <form onSubmit={handleSaveFreeformText}>
          <p className="dim-line">
            {draftTitle(detailDraft)}
            {detailDraft.compiled_at === null && (
              <>
                {' '}
                &mdash; NOT YET COMPILED INTO RULES; THE SCRIBE MAY REFUSE TO
                PROMOTE IT UNTIL IT IS.
              </>
            )}
          </p>

          <label className="form-row" htmlFor="draft-detail-freeform-text">
            <span>THY WISHES:</span>
            <textarea
              id="draft-detail-freeform-text"
              name="draft-detail-freeform-text"
              rows={8}
              value={detailFreeformText}
              onChange={(e) => setDetailFreeformText(e.target.value)}
              disabled={
                saveStatus === 'saving'
                || deleteStatus !== 'idle'
                || promoteStatus === 'promoting'
              }
            />
          </label>

          {detailFormError && <p className="error-line">! {detailFormError}</p>}
          {!detailFormError && detailNotice && (
            <p className="success-line">{detailNotice}</p>
          )}

          {deleteStatus === 'idle' && (
            <div className="button-row button-row--split">
              <button
                type="button"
                className="retro-button retro-button--ghost"
                onClick={backToListFromDetail}
                disabled={saveStatus === 'saving' || promoteStatus === 'promoting'}
              >
                &laquo; BACK
              </button>
              <div className="button-row">
                <button
                  type="submit"
                  className="retro-button"
                  disabled={saveStatus === 'saving' || promoteStatus === 'promoting'}
                >
                  {saveStatus === 'saving' ? 'SAVING...' : 'SAVE'}
                </button>
                <button
                  type="button"
                  className="retro-button retro-button--accent"
                  onClick={handlePromote}
                  disabled={saveStatus === 'saving' || promoteStatus === 'promoting'}
                >
                  {promoteStatus === 'promoting' ? 'PROMULGATING...' : 'PROMOTE'}
                </button>
                <button
                  type="button"
                  className="retro-button retro-button--danger"
                  onClick={startDelete}
                  disabled={saveStatus === 'saving' || promoteStatus === 'promoting'}
                >
                  DELETE
                </button>
              </div>
            </div>
          )}

          {deleteStatus === 'confirming' && (
            <>
              <p className="error-line">
                ! BURN THIS DRAFT FOR GOOD? THERE IS NO UNDOING IT.
              </p>
              <div className="button-row">
                <button
                  type="button"
                  className="retro-button retro-button--danger"
                  onClick={confirmDelete}
                >
                  YES, DELETE IT
                </button>
                <button
                  type="button"
                  className="retro-button retro-button--ghost"
                  onClick={cancelDelete}
                >
                  CANCEL
                </button>
              </div>
            </>
          )}

          {deleteStatus === 'deleting' && (
            <p className="dim-line">FEEDING THE DRAFT TO THE FLAMES...</p>
          )}
        </form>
      )}
    </div>
  );
}
